import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { formatRupiah, formatDateShort } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Search } from "lucide-react";

export default function OrderReports({ isTabbed = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/orders?status=diterima');
      setOrders(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const filtered = orders.filter((o) => {
    // filter by month and year
    const d = new Date(o.tanggal_masuk || o.created_at);
    if (d.getMonth() + 1 !== Number(month) || d.getFullYear() !== Number(year)) {
      return false;
    }

    if (!q) return true;
    const t = q.toLowerCase();
    return (
      o.order_number?.toLowerCase().includes(t) ||
      o.customer?.name?.toLowerCase().includes(t) ||
      o.customer?.phone?.toLowerCase().includes(t)
    );
  });

  const content = (
    <div className="space-y-6">
      {!isTabbed && (
        <header className="flex items-end justify-between border-b border-[#E5DCC7] pb-6 clf-rule">
          <div>
            <div className="clf-label mb-2">Laporan</div>
            <h1 className="text-3xl tracking-tight font-medium">Laporan Order</h1>
            <p className="text-sm text-stone-500 mt-1">Pesanan yang telah selesai dan diterima.</p>
          </div>
        </header>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.5} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari no order / customer..."
            className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-[#D9CDB5] focus:border-stone-900 focus:outline-none rounded-sm"
          />
        </div>
        <div className="flex items-center gap-2 border border-[#D9CDB5] bg-white p-1">
          <select value={month} onChange={(e) => setMonth(e.target.value)} className="px-3 py-1.5 text-sm bg-transparent focus:outline-none">
            {Array.from({length: 12}, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('id-ID', { month: 'long' })}</option>
            ))}
          </select>
          <select value={year} onChange={(e) => setYear(e.target.value)} className="px-3 py-1.5 text-sm bg-transparent focus:outline-none border-l border-[#E5DCC7]">
            {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-[#E5DCC7]">
        <table className="w-full text-sm">
          <thead className="border-b border-[#E5DCC7] bg-stone-50">
            <tr>
              <th className="text-left clf-label px-4 py-3">No. Order</th>
              <th className="text-left clf-label px-4 py-3">Customer</th>
              <th className="text-left clf-label px-4 py-3">Tgl Masuk</th>
              <th className="text-left clf-label px-4 py-3">Tgl Selesai</th>
              <th className="text-right clf-label px-4 py-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-stone-500">Memuat...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-stone-500">Belum ada laporan order di bulan ini.</td></tr>
            ) : (
              filtered.map((o) => (
                <tr key={o.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                  <td className="px-4 py-3 font-mono text-stone-900 font-medium">
                    <Link to={`/orders/${o.id}`} className="hover:text-[#A63D30]">
                      {o.order_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-stone-900">{o.customer?.name || "—"}</div>
                    <div className="text-[11px] text-stone-500">{o.customer?.phone}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-stone-700">{formatDateShort(o.tanggal_masuk)}</td>
                  <td className="px-4 py-3 font-mono text-stone-700">{formatDateShort(o.tanggal_selesai)}</td>
                  <td className="px-4 py-3 text-right font-mono font-medium">{formatRupiah(o.total)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (isTabbed) return content;
  
  return (
    <div className="p-4 lg:p-10 space-y-6 max-w-[1400px]">
      {content}
    </div>
  );
}
